import { ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

export interface Option {
  key: string;
  value: string;
}

interface SelectCheckBoxProps {
  id: string;
  name?: string;
  options: Option[];
  selectedOptions: string | null;
  onChange: (_selected: string) => void;
  label?: string;
  placeholder?: string;
  validationError?: boolean;
  disabled?: boolean;
  value?: string | null;
  textClass?: string;
  renderOptionLabel?: (_option: Option) => ReactNode;
}

const Select = ({
  id,
  options,
  selectedOptions,
  onChange,
  label,
  placeholder,
  validationError,
  disabled,
  value,
  textClass,
  renderOptionLabel,
}: SelectCheckBoxProps) => {
  const [open, setOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>(
    'bottom'
  );
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);

  const handleOnChange = (item: string) => {
    onChange(item);
    setOpen(false);
  };

  useEffect(() => {
    if (open) {
      const buttonRect = buttonRef.current?.getBoundingClientRect();
      const dropdownHeight = dropdownRef.current?.offsetHeight ?? 0;
      const spaceBelow = window.innerHeight - (buttonRect?.bottom ?? 0);
      const spaceAbove = buttonRect?.top ?? 0;

      if (spaceBelow < dropdownHeight && spaceAbove >= dropdownHeight) {
        setDropdownPosition('top');
      } else {
        setDropdownPosition('bottom');
      }
    }
  }, [open]);

  const convertStartToUppercase = (text: string) => {
    return `${text.charAt(0).toUpperCase()}${text.slice(1, text.length)}`;
  };

  return (
    <>
      {label && (
        <label htmlFor={id} className="block text-sm font-medium mb-2">
          {convertStartToUppercase(label)}
        </label>
      )}

      <div className="relative w-full">
        <button
          disabled={disabled}
          ref={buttonRef}
          type="button"
          className={`relative inline-flex justify-between items-center w-full py-[14px] ps-4 pe-2 border border-gray-200 bg-white rounded-lg text-gray-500 disabled:pointer-events-none disabled:bg-gray-100 ${
            open ? 'border-slate-500 z-50' : ''
          } ${validationError ? 'input-control-error' : ''}`}
          onClick={() => setOpen(!open)}
        >
          <span className={`text-sm ${textClass}`}>
            {selectedOptions
              ? (renderOptionLabel?.({
                  key: selectedOptions,
                  value: selectedOptions,
                }) ?? selectedOptions)
              : value
                ? (renderOptionLabel?.({ key: value, value }) ?? value)
                : convertStartToUppercase(placeholder ?? '')}
          </span>
          <span className="ml-2">
            <ChevronDown />
          </span>
        </button>
        {open && (
          <button
            type="button"
            onClick={() => setOpen(false)}
            tabIndex={-1}
            className="fixed inset-0 z-10"
          ></button>
        )}
        {open && (
          <ul
            ref={dropdownRef}
            className={`shadow-md max-h-60 overflow-y-auto absolute left-0 w-full flex flex-col rounded-lg bg-white border border-gray-200 text-gray-800 px-1 mt-3 ${
              open ? 'z-50' : ''
            } ${dropdownPosition === 'top' ? 'bottom-full mb-2' : 'mt-2'}`}
          >
            {options.map((item) => (
              <li
                className="hover:bg-slate-50 rounded-lg my-1 py-3 px-4 cursor-pointer"
                key={item.key}
                onClick={() => handleOnChange(item.value)}
              >
                <span className={`text-sm ${textClass}`}>
                  {renderOptionLabel ? renderOptionLabel(item) : item.value}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
};

export default Select;
